import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import withData from 'util/hoc/with-data';
import Loading from 'components/loading';
import { resolve } from 'library/helpers/path-resolver';
import styled from 'styled-components';
import Breadcrumbs, { BreadcrumbItem } from 'components/breadcrumbs';
import Card from 'apps/trimble-careers/components/card';
import CareersIcon from 'apps/journey/icons/education/decide/sign-post.svg';
import { AddViewedCareer } from 'apps/trimble-careers/components/viewed-careers';
import IndustryPageCardSection from 'apps/trimble-careers/components/careers/industry-page-card-section';
import CareerTidbits from 'apps/trimble-careers/components/career-tidbits';
import ZipSelect from 'apps/trimble-careers/components/zip-select';
import { TrackCareerView } from 'apps/trimble-careers/components/viewed-careers';

import Icons from 'apps/trimble-careers/components/icons/index';

const IndustryPageCardTitle = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.2;
  margin-bottom: 1em;

  > svg {
    width: 4em;
    height: 4em;
    margin-right: 1em;
  }
`;

const StyledSidePanel = styled.div`
  height: 81vh;
  overflow: scroll;
`;

const StyledIndustryPageCardData = styled.div`
  border: 1px solid #ccc;
  border-radius: 0.25em;
  position: relative;
`;

const StyledIndustryCard = styled.div`
  height: 81vh;
  overflow: scroll;
  padding: 2em;
  width: 768px;
  flex: 0 0 768px;
  margin-right: 1.5em;
  margin-bottom: 1em;
  @media (max-width: calc(1200px + 1em)) {
    flex: 1;
    max-width: 100%;
  }
`;

const Test = styled.svg`
  position: absolute;
  width: 5px;
  z-index: 1000;
`;

const DrawLines = ({ from: f, to = [] }) => {
  if (!f) return null;

  return (to || []).map((t, i) => {
    const left = Math.min(f.offsetLeft, t.offsetLeft);
    const width =
      Math.max(f.offsetLeft + f.offsetWidth, t.offsetLeft + t.offsetWidth) -
      left;
    const height = t.offsetTop - (f.offsetTop + f.offsetHeight);

    //
    // if (f.offsetLeft > t.offsetLeft) {
    //   svgLeft = t.offsetLeft + (t.offsetWidth / 2);
    // }
    const svgFrom = f.offsetLeft + f.offsetWidth / 2 - left;
    const svgTo = t.offsetWidth / 2 + (t.offsetLeft - left);
    const stretch = Math.min(80, height / 2);

    return (
      <Test
        key={i}
        style={{
          top: `${f.offsetTop + f.offsetHeight}px`,
          left: `${left}px`,
          width: `${width}px`,
          height: `${height}px`,
        }}
        viewBox={`0 0 ${width * 5} ${height * 5}`}
        preserveAspectRatio="none"
      >
        <defs>
          <radialGradient
            cx="50%"
            cy="50%"
            fx="50%"
            fy="50%"
            r="100%"
            id="radialGradient-1"
          >
            <stop stopColor="#c5c5c5" offset="0%"></stop>
            <stop stopColor="#c5c5c5" stopOpacity="0" offset="100%"></stop>
          </radialGradient>
        </defs>
        <g
          id="Artboard"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <path
            d={`M${svgFrom * 5},0 C${svgFrom * 5},${stretch * 5} ${svgTo * 5},${
              (height - stretch) * 5
            } ${svgTo * 5},${height * 5}`}
            stroke="url(#radialGradient-1)"
            strokeWidth="20"
            strokeLinecap="round"
            strokeDasharray="0,60"
          ></path>
        </g>
      </Test>
    );
  });
};

const StyledCareerCards = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-wrap: wrap;
`;

const StyledAboutHeader = styled.p`
  color: #207c9d;
  font-weight: bold;
`;

const StyledTitleH1 = styled.h1`
  font-size: 36px;
  margin: 0;
  line-height: 1;
  small {
    font-size: 20px;
  }
  span {
    color: ${(props) => props.color};
  }
`;

const styledSidebarCard = styled.div`
  margin-bottom: 1em;
  max-width: 420px;
  width: 100%;
`;

class CareersPage extends React.Component {
  state = {
    drawings: {
      from: null,
      to: [],
    },
    selected: null,
    zip: null,
  };

  constructor(...params) {
    super(...params);
    this.nodes = {};
  }

  handleClick = (career) => {
    if (!career) {
      this.setState({
        selected: null,
        drawings: {
          to: [],
          from: null,
        },
      });
      return;
    }

    TrackCareerView({
      name: career.name,
      id: career.id,
      career_name: resolve(this.props, 'graph_data/career/name', ''),
      career_slug: resolve(this.props, 'graph_data/career/slug', ''),
      industry_name: resolve(this.props, 'graph_data/career/industry_name', ''),
      industry_slug: resolve(this.props, 'graph_data/career/industry_slug', ''),
      color: resolve(this.props, 'graph_data/career/color', ''),
    });

    this.setState({
      selected: career,
    });
    const jobs = resolve(this.props, 'graph_data/career/jobs', {});
    let currentLevel = -1;
    let nextLevel = -1;
    for (const level in jobs) {
      const c = jobs[level].findIndex((l) => {
        if (l.type === 'group') {
          return (
            l.careers.findIndex((s) => {
              s.id === career.id;
            }) > -1
          );
        }
        return l.id === career.id;
      });

      if (c != -1) {
        currentLevel = Number(level);
        break;
      }
    }

    for (let i = currentLevel + 1; i <= 8 && i != -1; i++) {
      if (jobs[i]) {
        nextLevel = i;
        break;
      }
    }

    if (nextLevel > -1 && nextLevel <= 8) {
      let currentNode = this.nodes[career.name];
      let nextNodes = [];

      for (const career of jobs[nextLevel]) {
        nextNodes.push(this.nodes[career.name]);
      }

      this.setState({
        drawings: {
          from: currentNode,
          to: nextNodes,
        },
      });
    } else {
      this.setState({
        drawings: {
          from: [],
          to: null,
        },
      });
    }
  };

  handleZipChange = (e) => {
    this.setState({
      zip: e.target.value,
    });
  };

  render() {
    const { graph_loading, graph_data } = this.props;

    if (graph_loading) {
      return <Loading />;
    }

    const IconComponent = resolve(
      Icons,
      resolve(graph_data, 'career/industry_slug', ''),
      Icons.education
    );

    return (
      <Fragment>
        <Breadcrumbs>
          <BreadcrumbItem to="/trimble">All Paths</BreadcrumbItem>
          <BreadcrumbItem
            to={`/trimble/${resolve(graph_data, 'career/industry_slug', '')}`}
          >
            {resolve(graph_data, 'career/industry_name', '')}
          </BreadcrumbItem>
          <BreadcrumbItem
            to={`/trimble/${resolve(
              graph_data,
              'career/industry_slug',
              ''
            )}/${resolve(graph_data, 'career/slug', '')}`}
          >
            {resolve(graph_data, 'career/name', '')}
          </BreadcrumbItem>
        </Breadcrumbs>
        <StyledCareerCards>
          <Card
            as={StyledIndustryCard}
            color={resolve(graph_data, 'career/color', '')}
          >
            <IndustryPageCardTitle>
              <IconComponent color={resolve(graph_data, 'career/color', '')} />
              <StyledTitleH1 color={resolve(graph_data, 'career/color', '')}>
                <small>{resolve(graph_data, 'career/industry_name', '')}</small>
                <span>{resolve(graph_data, 'career/name', '')}</span>
              </StyledTitleH1>
            </IndustryPageCardTitle>
            <StyledIndustryPageCardData>
              <IndustryPageCardSection
                label=""
                nodes={this.nodes}
                onClick={this.handleClick}
                zip={this.state.zip}
                color={resolve(graph_data, 'career/color', '')}
                industry={resolve(graph_data, 'career/industry_slug', '')}
                data={[
                  resolve(graph_data, 'career/jobs/0', []),
                  resolve(graph_data, 'career/jobs/1', []),
                  resolve(graph_data, 'career/jobs/2', []),
                ]}
              />
              <IndustryPageCardSection
                label=""
                nodes={this.nodes}
                onClick={this.handleClick}
                zip={this.state.zip}
                color={resolve(graph_data, 'career/color', '')}
                industry={resolve(graph_data, 'career/industry_slug', '')}
                data={[
                  resolve(graph_data, 'career/jobs/3', []),
                  resolve(graph_data, 'career/jobs/4', []),
                  resolve(graph_data, 'career/jobs/5', []),
                ]}
              />
              <IndustryPageCardSection
                label=""
                nodes={this.nodes}
                onClick={this.handleClick}
                zip={this.state.zip}
                color={resolve(graph_data, 'career/color', '')}
                industry={resolve(graph_data, 'career/industry_slug', '')}
                data={[
                  resolve(graph_data, 'career/jobs/6', []),
                  resolve(graph_data, 'career/jobs/7', []),
                  resolve(graph_data, 'career/jobs/8', []),
                ]}
              />
              <DrawLines
                from={this.state.drawings.from}
                to={this.state.drawings.to}
              />
            </StyledIndustryPageCardData>
          </Card>
          <StyledSidePanel>
            {this.state.selected ? (
              <CareerTidbits data={this.state.selected} zip={this.state.zip} />
            ) : null}
            <Card as={styledSidebarCard} color="#207C9D">
              <h3>About this Pathway</h3>
              <p>{resolve(graph_data, 'career/description', '')}</p>
              {resolve(graph_data, 'career/career_expected_pay', false) ? (
                <Fragment>
                  <StyledAboutHeader>
                    What kind of pay is expected in this pathway?
                  </StyledAboutHeader>
                  <p>{resolve(graph_data, 'career/career_expected_pay', '')}</p>
                  <hr />
                </Fragment>
              ) : null}
              {resolve(graph_data, 'career/career_buzz', false) ? (
                <Fragment>
                  <StyledAboutHeader>{`What's the big buzz in this pathway?`}</StyledAboutHeader>
                  <p>{resolve(graph_data, 'career/career_buzz', '')}</p>
                  <hr />
                </Fragment>
              ) : null}
              {resolve(graph_data, 'career/career_companies', false) ? (
                <Fragment>
                  <StyledAboutHeader>
                    What types of companies do you work for?
                  </StyledAboutHeader>
                  <p>{resolve(graph_data, 'career/career_companies', '')}</p>
                  <hr />
                </Fragment>
              ) : null}
              {resolve(graph_data, 'career/career_workers', false) ? (
                <Fragment>
                  <StyledAboutHeader>
                    Who likes to work in this industry?
                  </StyledAboutHeader>
                  <p>{resolve(graph_data, 'career/career_workers', '')}</p>
                </Fragment>
              ) : null}
            </Card>
          </StyledSidePanel>
        </StyledCareerCards>
      </Fragment>
    );
  }
}

CareersPage.propTypes = {
  match: PropTypes.object,
  graph_loading: PropTypes.bool,
  graph_data: PropTypes.object,
};

CareersPage.query = async (cancelToken, { match }) =>
  await fetch(
    `https://fmzhs9h4ah.execute-api.us-east-1.amazonaws.com/trimble/industries/${match.params.industry}/${match.params.career}`
  )
    .then((r) => r.json())
    .then((r) => ({ data: { career: r } }));

export default withData(CareersPage);
